import React, { useCallback } from "react";
import { IDMobile } from "../../../components/IDs/IDs";
import ReactTooltip from "react-tooltip";
import { FiltersDropDown } from "../../../components/FiltersDropDown/FiltersDropDown";
import { TablePageLayout } from "../../../components/TablePage/TablePageLayout";
import type {
  IQuotesAPIResponse,
  IQuoteRequest,
  StatusAndId,
  QuoteStatus,
  QuoteRequestItem,
} from "../../../types/types";
import { PageWrapper } from "../../../layout/portalPageLayout";
import {
  DEFAULT_PER_PAGE,
  formatDateTime,
  useInAppNotifications,
  useStoreState,
} from "../../../util/util";
import { useRoutePath } from "../../../util/Routing";
import { useAuthenticatedUser } from "../../../hooks/useAuthenticatedUser";
import { useTranslation } from "react-i18next";
import { calculatePriceDetails } from "../../../components/PriceDetails/PriceDetails";
import { TooltipCell } from "../BuyerOrders/BuyerOrders.util";
import { TrimmedName } from "../../admin/SellerAdmin/PIM/SellarAdminPIMAssets/util/AssetsUtil";
import type { Getter } from "@tanstack/react-table";
import {
  BuyerQuoteStatus,
  getBuyerQuoteStatusFilters,
  getQuoteItemsList,
  getQuoteProductsAsText,
} from "../../../components/QuoteStatus";
import { useListPage } from "../../SharedPages/shared";

type QuotesTable = {
  id: string;
  products: { items: QuoteRequestItem[]; id: string };
  total_value: string;
  last_activity: string;
  status: StatusAndId<QuoteStatus>;
  unread_message_count?: number;
};

export const BuyerQuotesList = () => {
  const user = useAuthenticatedUser();
  const { t } = useTranslation();
  const { storefront_id } = useStoreState();
  const { quotesNotifications } = useInAppNotifications(storefront_id, user);
  const { accountPath } = useRoutePath();

  const columns = [
    {
      header: t("Quote ID"),
      accessorKey: "number",
      id: "number",
      cell: ({ getValue }: { getValue: Getter<string> }) => (
        <IDMobile>{`${getValue()}`}</IDMobile>
      ),
    },
    {
      header: t("Product(s)"),
      accessorKey: "products",
      id: "products",
      cell: ({
        getValue,
      }: {
        getValue: Getter<{ items: QuoteRequestItem[]; id: string }>;
      }) => (
        <TooltipCell data-for={`${getValue().id}-tooltip`} data-tip={""}>
          <TrimmedName text={getQuoteProductsAsText(getValue().items, t)} />
          <ReactTooltip
            id={`${getValue().id}-tooltip`}
            place="top"
            data-html={true}
            effect="solid"
            backgroundColor="#60676F"
            multiline={true}
          >
            {getQuoteItemsList(getValue().items)}
          </ReactTooltip>
        </TooltipCell>
      ),
    },
    {
      header: t("Total Value"),
      accessorKey: "total_value",
      id: "total_value",
    },
    {
      header: t("Last Activity"),
      accessorKey: "last_activity",
      id: "last_activity",
    },
    {
      header: t("Status"),
      accessorKey: "status",
      id: "status",
      size: 50,
      minSize: 50,
      align: "right",
      cell: ({ getValue }: { getValue: Getter<StatusAndId<QuoteStatus>> }) => (
        <BuyerQuoteStatus status={getValue().status} id={getValue().id} t={t} />
      ),
    },
  ];

  const dataTransformer = useCallback(
    (response: IQuotesAPIResponse): QuotesTable[] =>
      response.data.map((quote: IQuoteRequest) => ({
        id: quote.id,
        number: quote.number,
        products: { items: quote.items, id: quote.id },
        total_value: quote.items.length
          ? (() => {
              const { totalString } = calculatePriceDetails({
                items: quote.items,
                fees: quote.fees,
                totalDiscount: quote.total_discounts,
                currencyCode: quote.items[0].currency,
              });
              return totalString;
            })()
          : "-/-",
        last_activity: formatDateTime(quote.modified_at) || "--",
        status: { status: quote.status || "--", id: quote.id },
        unread_message_count: quote.unread_message_count,
      })),
    []
  );

  const paginationTransformer = useCallback(
    (response: IQuotesAPIResponse) => ({
      total: response.pagination.total,
      offset: response.pagination.offset,
    }),
    []
  );

  const getUnreadIds = useCallback(
    (quote: QuotesTable) =>
      quotesNotifications?.ids
        ? quotesNotifications.ids.includes(quote.id)
        : false,
    [quotesNotifications]
  );

  const {
    state,
    data: tableData,
    isLoading,
    error,
    handlers,
    tableColumns,
  } = useListPage<QuotesTable, IQuotesAPIResponse>({
    defaultPerPage: DEFAULT_PER_PAGE,
    perPageOptions: [10, 20, 50, 100],
    endpoint: `/v1/storefronts/${storefront_id}/quotes`,
    requestParams: { order_by: "desc" },
    dataTransformer,
    paginationTransformer,
    getUnreadIds,
    notifications: quotesNotifications,
    statusFilters: getBuyerQuoteStatusFilters(t),
    accountManagerFilters: [],
    customerServiceRepFilters: [],
    columns,
    custom_columns: { unread_message_count: 4 },
    basePath: `${accountPath}/quotes`,
    searchPlaceholder: t("Search by Product or Quote ID"),
  });

  const handleRowClick = (
    e: React.MouseEvent<HTMLTableRowElement, MouseEvent>
  ) => {
    handlers.handleRowClick(e, e.currentTarget.id, `${accountPath}/quotes`);
  };

  return (
    <PageWrapper>
      <TablePageLayout
        showTitle
        title={t("Quotes")}
        searchQuery={state.searchQuery}
        searchPlaceholder={t("Search by Product or Quote ID")}
        onSearchChange={handlers.handleSearch}
        onSearchClear={handlers.handleClearSearch}
        filtersSlot={
          <FiltersDropDown
            activeItems={state.selectedStatus}
            applyStatus={handlers.applyStatus}
            list={getBuyerQuoteStatusFilters(t)}
          />
        }
        perPage={state.perPage}
        perPageItems={state.perPageItems}
        onPerPageChange={handlers.changePerPage}
        columns={tableColumns}
        data={tableData}
        isLoading={isLoading}
        error={error}
        rowClick={handleRowClick}
        pagination={state.tablePagination}
        totalCount={state.tablePagination.totalCount}
        offset={state.offset}
        onPageChange={handlers.changePage}
      />
    </PageWrapper>
  );
};
